<template>
  <div class="p-4">
    <!-- Sin contratos -->
    <div v-if="contracts.length === 0" class="text-center py-5">
      <i class="ri-file-list-line text-muted" style="font-size: 3rem;"></i>
      <p class="text-muted mt-3 mb-4">Este colaborador no tiene contratos vigentes.</p>
      <router-link
        :to="{ name: 'contracts-create', query: { employeeId: employeeId } }"
        class="button button-primary"
      >
        <i class="ri-add-line mr-1"></i>Crear contrato
      </router-link>
    </div>

    <!-- Con contrato(s) -->
    <template v-else>
      <!-- Selector de contrato (solo si hay más de 1) -->
      <div v-if="contracts.length > 1" class="d-flex flex-wrap mb-4 contract-pills">
        <button
          v-for="(c, idx) in contracts"
          :key="c.id"
          type="button"
          :class="['button button-sm mr-2 mb-2', activeContractIndex === idx ? 'button-primary' : 'button-light']"
          @click="activeContractIndex = idx"
        >
          <i class="ri-building-line mr-1"></i>{{ companyOf(c) }}
          <span class="ml-1 text-muted small" v-if="c.contractNumber">N° {{ c.contractNumber }}</span>
        </button>
      </div>

      <div class="d-flex justify-content-between align-items-start mb-4">
        <div>
          <span :class="`badge ${contractStatusBadge.color}`">{{ contractStatusBadge.label }}</span>
          <span v-if="currentContract.contractNumber" class="ml-2 text-muted small">
            N° {{ currentContract.contractNumber }}
          </span>
        </div>
        <div class="d-flex">
          <router-link
            :to="{ name: 'contracts-list' }"
            class="button button-light button-sm mr-2"
          >
            Ver todos los contratos
          </router-link>
          <button
            type="button"
            class="button button-light button-sm mr-2"
            @click="downloadDocument"
            :disabled="downloadingDoc"
          >
            <i class="ri-file-word-line mr-1"></i>
            {{ downloadingDoc ? 'Generando...' : 'Generar documento' }}
          </button>
          <router-link
            :to="{ name: 'contracts-edit', params: { id: currentContract.id } }"
            class="button button-primary button-sm"
          >
            <i class="ri-edit-line mr-1"></i>Editar contrato
          </router-link>
        </div>
      </div>

      <!-- Empresa / Sede / Área -->
      <div class="row mb-3">
        <div class="col-md-4">
          <small class="text-muted d-block">Empresa</small>
          <strong>{{ companyName }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Sede</small>
          <strong>{{ workSiteName }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Área</small>
          <strong>{{ areaName }}</strong>
        </div>
      </div>

      <!-- Cargo / Jefe / Email -->
      <div class="row mb-3">
        <div class="col-md-4">
          <small class="text-muted d-block">Cargo</small>
          <strong>{{ positionName }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Jefe directo</small>
          <strong>{{ supervisorName }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Email corporativo</small>
          <strong>{{ currentContract.corporateEmail || '---' }}</strong>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-4">
          <small class="text-muted d-block">Canal</small>
          <strong>{{ currentContract.channelName || '---' }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Línea de gasto</small>
          <strong>{{ currentContract.expenseLineName || '---' }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Tipos de planilla</small>
          <template v-if="currentContract.payrollTypeNames && currentContract.payrollTypeNames.length">
            <span v-for="(name, idx) in currentContract.payrollTypeNames" :key="idx"
              class="badge badge-info mr-1">{{ name }}</span>
          </template>
          <strong v-else>---</strong>
        </div>
      </div>

      <!-- Fechas / Tipo contrato -->
      <div class="row mb-3">
        <div class="col-md-4">
          <small class="text-muted d-block">Fecha inicio</small>
          <strong>{{ currentContract.startDate || '---' }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Fecha fin</small>
          <strong>{{ currentContract.endDate || 'Indeterminado' }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Tipo de contrato</small>
          <strong>{{ currentContract.contractType || '---' }}</strong>
        </div>
      </div>

      <!-- Sueldo / Régimen / Jornada -->
      <div class="row mb-3">
        <div class="col-md-4">
          <small class="text-muted d-block">Sueldo base</small>
          <strong>{{ formatSalary }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Régimen laboral</small>
          <strong>{{ currentContract.laborRegime || '---' }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Jornada</small>
          <strong>
            {{ currentContract.workSchedule || '---' }}
            <span v-if="currentContract.weeklyHours"> ({{ currentContract.weeklyHours }}h/sem)</span>
          </strong>
        </div>
      </div>

      <!-- Beneficios -->
      <div class="row mb-3">
        <div class="col-md-4">
          <small class="text-muted d-block">Asignación familiar</small>
          <strong>{{ boolLabel(currentContract.hasFamilyAllocation) }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">EPS</small>
          <strong>{{ boolLabel(currentContract.hasEps) }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">SCTR (Pensión / Salud)</small>
          <strong>{{ boolLabel(currentContract.hasSctrPension) }} / {{ boolLabel(currentContract.hasSctrSalud) }}</strong>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-4">
          <small class="text-muted d-block">Seguro de vida ley</small>
          <strong>{{ boolLabel(currentContract.hasLifeInsurance) }}</strong>
        </div>
        <div class="col-md-4">
          <small class="text-muted d-block">Código EsSalud</small>
          <strong>{{ currentContract.essaludCode || '---' }}</strong>
        </div>
        <div class="col-md-4" v-if="currentContract.schedule">
          <small class="text-muted d-block">Horario</small>
          <strong>{{ currentContract.schedule.name || '---' }}</strong>
        </div>
      </div>

      <!-- Documento firmado -->
      <hr class="my-4" />
      <h6 class="text-muted mb-3">Documento firmado</h6>

      <template v-if="currentContract.hasSignedDocument">
        <div class="d-flex align-items-center">
          <i :class="signedDocIcon" class="mr-2 text-primary" style="font-size: 1.5rem;"></i>
          <div class="flex-grow-1">
            <strong>{{ currentContract.signedDocumentName }}</strong>
            <div class="text-muted small" v-if="currentContract.signedDocumentDate">
              Subido el {{ currentContract.signedDocumentDate.substring(0, 10) }}
            </div>
          </div>
          <button type="button" class="button button-light button-sm mr-2" @click="downloadSignedDoc" :disabled="downloadingSigned">
            <i class="ri-download-line mr-1"></i>{{ downloadingSigned ? 'Descargando...' : 'Descargar' }}
          </button>
          <button type="button" class="button button-light button-sm" @click="confirmReplace">
            <i class="ri-refresh-line mr-1"></i>Reemplazar
          </button>
        </div>
      </template>

      <template v-else>
        <div class="d-flex align-items-center">
          <span class="text-muted mr-3">No se ha adjuntado el contrato firmado.</span>
          <button type="button" class="button button-primary button-sm" @click="$refs.signedFileInput.click()">
            <i class="ri-upload-2-line mr-1"></i>Adjuntar contrato firmado
          </button>
        </div>
      </template>

      <input
        type="file"
        ref="signedFileInput"
        accept=".pdf,.jpg,.jpeg,.png"
        style="display: none;"
        @change="onSignedFileSelected"
      />
    </template>
  </div>
</template>

<script>
import { Alert, Util } from 'wize-admin'
import { ContractsService } from '@/core/services'
import fileDownload from 'js-file-download'

export default {
  name: 'ContractSummaryTab',
  props: {
    employeeId:       { type: Number, default: null },
    currentContracts: { type: Array,  default: () => [] },
  },
  data() {
    return {
      downloadingDoc: false,
      downloadingSigned: false,
      activeContractIndex: 0,
    }
  },
  watch: {
    currentContracts() {
      // Si la lista cambió de tamaño, resetear al primero
      if (this.activeContractIndex >= this.currentContracts.length) {
        this.activeContractIndex = 0
      }
    },
  },
  computed: {
    contracts() {
      return Array.isArray(this.currentContracts) ? this.currentContracts : []
    },
    currentContract() {
      return this.contracts[this.activeContractIndex] || null
    },
    contractStatusBadge() {
      const c = this.currentContract
      if (!c) return { label: 'VIGENTE', color: 'badge-green' }
      if (c.status === 5) return { label: 'VENCIDO', color: 'badge-warning' }
      // Fallback visual por si el job no corrió
      if (c.endDate && new Date(c.endDate) < new Date()) {
        return { label: 'VENCIDO', color: 'badge-warning' }
      }
      if (c.finishDate && new Date(c.finishDate) < new Date()) {
        return { label: 'VENCIDO', color: 'badge-warning' }
      }
      return { label: 'VIGENTE', color: 'badge-green' }
    },
    companyName() {
      const c = this.currentContract
      if (!c) return '---'
      return (c.company && c.company.name) || c.companyName || '---'
    },
    workSiteName() {
      const c = this.currentContract
      if (!c) return '---'
      return (c.workSite && (c.workSite.name || c.workSite.description)) || c.workSiteName || '---'
    },
    areaName() {
      const c = this.currentContract
      if (!c) return '---'
      return (c.area && c.area.description) || c.areaName || '---'
    },
    positionName() {
      const c = this.currentContract
      if (!c) return '---'
      return (c.position && c.position.description) || c.positionName || '---'
    },
    supervisorName() {
      const c = this.currentContract
      if (!c) return '---'
      if (c.supervisor) {
        const s = c.supervisor
        return [s.name, s.lastNamePaternal].filter(Boolean).join(' ') || '---'
      }
      return c.supervisorName || '---'
    },
    signedDocIcon() {
      const name = (this.currentContract.signedDocumentName || '').toLowerCase()
      if (name.endsWith('.pdf')) return 'ri-file-pdf-line'
      if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png')) return 'ri-image-line'
      return 'ri-file-line'
    },
    formatSalary() {
      const c = this.currentContract
      if (!c || c.salary == null) return '---'
      const currency = c.salaryType || c.salaryCurrency || 'PEN'
      return `${currency} ${Number(c.salary).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`
    },
  },
  methods: {
    companyOf(c) {
      if (!c) return '---'
      return (c.company && c.company.name) || c.companyName || '---'
    },
    async downloadDocument() {
      try {
        this.downloadingDoc = true
        const response = await ContractsService.downloadDocument(this.currentContract.id)
        fileDownload(response.data, Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error('Error al generar el documento del contrato')
      } finally {
        this.downloadingDoc = false
      }
    },
    boolLabel(val) {
      if (val === true  || val === 1) return 'Sí'
      if (val === false || val === 0) return 'No'
      return '---'
    },
    async downloadSignedDoc() {
      try {
        this.downloadingSigned = true
        const response = await ContractsService.downloadSignedDocument(this.currentContract.id)
        const name = this.currentContract.signedDocumentName || 'contrato-firmado'
        fileDownload(response.data || response, name)
      } catch (error) {
        console.error(error)
        Alert.error('Error al descargar el documento firmado')
      } finally {
        this.downloadingSigned = false
      }
    },
    async confirmReplace() {
      const ok = confirm('¿Desea reemplazar el documento firmado actual?')
      if (ok) this.$refs.signedFileInput.click()
    },
    async onSignedFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 10 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 10MB')
        this.$refs.signedFileInput.value = ''
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await ContractsService.uploadSignedDocument(this.currentContract.id, file)
        const updated = resp.payload
        this.currentContract.signedDocumentPath = updated.signedDocumentPath
        this.currentContract.signedDocumentName = updated.signedDocumentName
        this.currentContract.signedDocumentDate = updated.signedDocumentDate
        this.currentContract.hasSignedDocument = updated.hasSignedDocument
        Alert.success('Documento firmado adjuntado correctamente')
      } catch (error) {
        console.error(error)
        Alert.error('Error al subir el documento firmado')
      } finally {
        this.$store.dispatch('app/loading', false)
        this.$refs.signedFileInput.value = ''
      }
    },
  },
}
</script>

<style lang="stylus" scoped></style>
